import * as T from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { GTAOPass } from 'three/addons/postprocessing/GTAOPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { createModel, plant, ROWS, COLORS, gridPoint, trayPoint } from './model.js';

const $=id=>document.getElementById(id), engine=window.KidstepEngine;
const reduced=matchMedia('(prefers-reduced-motion: reduce)').matches;
let notifyTimer;
function notify(text,hold=false){clearTimeout(notifyTimer);$('hint').textContent=text;if(!hold)notifyTimer=setTimeout(()=>{$('hint').textContent='拖动空白旋转 · 滚轮 / 双指缩放';},4500);}
async function action(fn){try{await fn();}catch(e){notify(e.message||'声音暂时没有加载成功，请再试一次');}}

init().catch(error=>{
  console.error(error);
  $('loading').innerHTML='';
  const box=document.createElement('div');box.textContent='三维画面暂时无法启动。';
  const link=document.createElement('a');link.href='/classic';link.textContent=' 打开简易版 ↗';box.append(link);$('loading').append(box);
});
async function init(){
  if(!engine)throw new Error('Kidstep engine unavailable');
  const canvas=$('canvas'),stage=$('stage');
  const renderer=new T.WebGLRenderer({canvas,antialias:true,alpha:false,powerPreference:'high-performance'});
  const smallScreen=matchMedia('(max-width: 600px)').matches;
  renderer.setPixelRatio(Math.min(devicePixelRatio,smallScreen?1.25:1.6));
  renderer.shadowMap.enabled=true;renderer.shadowMap.type=T.PCFShadowMap;
  // Neutral tone mapping keeps the pastel paint colours true, like product photography.
  renderer.toneMapping=T.NeutralToneMapping;renderer.toneMappingExposure=1.0;
  const BACKDROP=[0xd3e0e4,0x98adb8];
  const scene=new T.Scene();scene.background=new T.Color(BACKDROP[1]);
  const camera=new T.PerspectiveCamera(32,1,.3,120);
  const controls=new OrbitControls(camera,canvas);controls.enableDamping=!reduced;controls.dampingFactor=.075;
  controls.disconnect();controls.minDistance=8;controls.maxDistance=90;controls.maxPolarAngle=Math.PI*.88;controls.minPolarAngle=.02;controls.enablePan=false;controls.rotateSpeed=.65;
  // Studio reflections: a warm softbox upper left, a cool strip right, a white ceiling bounce.
  const pmrem=new T.PMREMGenerator(renderer),env=pmrem.fromScene(studioEnvironment(),.03);
  scene.environment=env.texture;scene.environmentIntensity=.55;pmrem.dispose();
  scene.add(new T.HemisphereLight(0xeaf3ff,0x8a7a62,.32));
  const key=new T.DirectionalLight(0xfff0d9,2.3);key.position.set(-7,11,6);key.castShadow=true;
  Object.assign(key.shadow.camera,{left:-10,right:12,top:9,bottom:-9,near:.5,far:34});key.shadow.mapSize.setScalar(smallScreen?1024:2048);key.shadow.normalBias=.02;key.shadow.bias=-.0001;key.shadow.radius=6;scene.add(key);
  const fill=new T.DirectionalLight(0xd6e6f5,.45);fill.position.set(8,5,5);scene.add(fill);
  const rimLight=new T.DirectionalLight(0xfff4e2,1.0);rimLight.position.set(4,8,-8);scene.add(rimLight);
  // Seamless backdrop: an unlit radial gradient that fades into the background, plus a shadow catcher.
  const backdropCanvas=document.createElement('canvas');backdropCanvas.width=backdropCanvas.height=512;
  {const c=backdropCanvas.getContext('2d'),g=c.createRadialGradient(256,256,20,256,256,256);g.addColorStop(0,'#'+new T.Color(BACKDROP[0]).getHexString());g.addColorStop(1,'#'+new T.Color(BACKDROP[1]).getHexString());c.fillStyle=g;c.fillRect(0,0,512,512);}
  const backdropMap=new T.CanvasTexture(backdropCanvas);backdropMap.colorSpace=T.SRGBColorSpace;
  const floor=new T.Mesh(new T.CircleGeometry(60,64),new T.MeshBasicMaterial({map:backdropMap,toneMapped:false}));floor.rotation.x=-Math.PI/2;floor.position.set(2,-.03,1);scene.add(floor);
  const catcher=new T.Mesh(new T.PlaneGeometry(60,60),new T.ShadowMaterial({color:0x2c3a34,opacity:.26}));catcher.rotation.x=-Math.PI/2;catcher.position.y=-.02;catcher.receiveShadow=true;catcher.userData.aoIgnore=true;scene.add(catcher);
  const model=await createModel(scene,MODEL_URL);
  if(/[?&]debug\b/.test(location.search))window.__kidstep={scene,renderer,model,camera};
  // A broad contact gradient supplements the real directional shadows.
  const shadowCanvas=document.createElement('canvas');shadowCanvas.width=shadowCanvas.height=256;const sc=shadowCanvas.getContext('2d');const gradient=sc.createRadialGradient(128,128,25,128,128,125);gradient.addColorStop(0,'rgba(65,57,34,.23)');gradient.addColorStop(1,'rgba(65,57,34,0)');sc.fillStyle=gradient;sc.fillRect(0,0,256,256);
  const contact=new T.Mesh(new T.PlaneGeometry(14,9),new T.MeshBasicMaterial({map:new T.CanvasTexture(shadowCanvas),transparent:true,depthWrite:false}));contact.rotation.x=-Math.PI/2;contact.position.y=-.020;contact.userData.aoIgnore=true;scene.add(contact);
  // Contact occlusion grounds pegs, rims and joinery. Ignore printed decals and hit proxies.
  class ContactAO extends GTAOPass {
    _overrideVisibility(){super._overrideVisibility();this.scene.traverse(o=>{if(o.visible&&o.userData.aoIgnore){o.visible=false;this._visibilityCache.push(o);}});}
  }
  const composer=new EffectComposer(renderer);composer.addPass(new RenderPass(scene,camera));
  composer.renderTarget1.samples=smallScreen?2:4;composer.renderTarget2.samples=smallScreen?2:4;
  const ao=new ContactAO(scene,camera,stage.clientWidth,stage.clientHeight);
  ao.updateGtaoMaterial({radius:.42,thickness:.8,distanceExponent:1.5,distanceFallOff:.8,scale:1.1,samples:smallScreen?8:16});
  ao.updatePdMaterial({radius:5,rings:2,samples:8});ao.blendIntensity=.70;
  // Soft glow on the lit step lights, pads and house window only (threshold above paint brightness).
  const bloom=new UnrealBloomPass(new T.Vector2(stage.clientWidth,stage.clientHeight),.35,.45,2.2);
  composer.addPass(ao);composer.addPass(bloom);composer.addPass(new OutputPass());
  let cameraGoal=null,selection=null,pointer=null,hover=null;
  // Render only while something changes, and step quality down on slow GPUs (common on Windows laptops).
  let dirty=3,quality=/[?&]quality=low\b/.test(location.search)?2:0;const slowFrames=[];
  function invalidate(){dirty=Math.max(dirty,2);}
  function degrade(level){
    quality=level;ao.enabled=false;bloom.enabled=false;
    if(level>=2){renderer.setPixelRatio(1);composer.setPixelRatio(1);composer.renderTarget1.samples=composer.renderTarget2.samples=0;key.shadow.mapSize.setScalar(1024);key.shadow.map?.dispose();key.shadow.map=null;resize();}
    invalidate();
  }
  const raycaster=new T.Raycaster(),mouse=new T.Vector2(),dragPlane=new T.Plane(new T.Vector3(0,1,0),-1.2),intersection=new T.Vector3();
  raycaster.layers.enable(1);
  function setRay(e){const r=canvas.getBoundingClientRect();mouse.set((e.clientX-r.left)/r.width*2-1,-(e.clientY-r.top)/r.height*2+1);raycaster.setFromCamera(mouse,camera);}
  function pick(e,targetsOnly=false){
    setRay(e);
    const hits=raycaster.intersectObjects(pickables,false);
    for(const hit of hits){
      let o=hit.object, visible=true, heldPiece=false;
      for(let ancestor=o;ancestor;ancestor=ancestor.parent){if(!ancestor.visible)visible=false;if(ancestor.userData.kind==='piece'&&ancestor.userData.id===selection)heldPiece=true;}
      if(!visible || (targetsOnly&&heldPiece))continue;
      while(o && !o.userData.kind)o=o.parent;
      if(o?.userData.kind){
        if(targetsOnly&&!['hole','tray'].includes(o.userData.kind))return null;
        return{...o.userData,object:o,point:hit.point};
      }
    }return null;
  }
  function view(top=false,instant=false){
    const aspect=stage.clientWidth/stage.clientHeight;
    const distance=Math.min(88,Math.max(24.5,9.0/(Math.tan(T.MathUtils.degToRad(camera.fov/2))*aspect)));
    controls.target.copy(model.center);
    const direction=top?new T.Vector3(0,1,.025):new T.Vector3(-4.4,12.2,17.5).normalize();
    const pos=direction.multiplyScalar(distance).add(controls.target);
    if(instant||reduced){camera.position.copy(pos);controls.update();cameraGoal=null;}else cameraGoal=pos;
  }
  function resize(){invalidate();renderer.setSize(stage.clientWidth,stage.clientHeight,false);composer.setSize(stage.clientWidth,stage.clientHeight);camera.aspect=stage.clientWidth/stage.clientHeight;camera.updateProjectionMatrix();}
  resize();view(false,true);new ResizeObserver(resize).observe(stage);
  controls.addEventListener('start',()=>cameraGoal=null);
  $('home').onclick=()=>view(false);$('top').onclick=()=>view(true);
  const zoom=f=>{cameraGoal=null;const delta=camera.position.clone().sub(controls.target);delta.setLength(T.MathUtils.clamp(delta.length()*f,controls.minDistance,controls.maxDistance));camera.position.copy(controls.target).add(delta);controls.update();};
  

  // Raycast only simple hit shapes: the full model is ~500k triangles and made pointer moves stall on slower PCs.
  const pickables=[...model.picks];model.cardStack.forEach(g=>g.traverse(o=>{if(o.isMesh&&!o.material.visible)pickables.push(o);}));
  const allBlocks=[...engine.state.tray];
  for(const b of allBlocks){const p=plant(b.c,b.id+(b.c===3?1:0));pickables.push(p.userData.hit);p.userData={kind:'piece',id:b.id,row:b.c};p.position.copy(trayPoint(b.id));model.root.add(p);model.pieces.set(b.id,p);}
  function sync(){
    const state=engine.state;
    canvas.setAttribute('aria-label',`Kidstep，${state.power?(state.playing?'播放中':'已停止'):'电源关闭'}，${Math.round(state.bpm)} BPM。拖动积木摆节奏；A S D F 敲鼓垫；空格播放。`);
    for(const [id,p]of model.pieces){
      if(engine.held?.block.id===id)continue;
      const place=engine.locate(id);if(!place)continue;
      const pos=place.track?gridPoint(ROWS.indexOf(place.track),place.step):trayPoint(id);
      p.userData.target=pos;p.rotation.y=place.track?0:((id*7)%11-5)*.07;
    }
    const card=engine.cards.find(c=>c.id===state.cardId);
    model.card.visible=model.tab.visible=model.tabLabel.visible=!!card;
    model.tabLabel.userData.setText(state.face);
    model.playMark.userData.setText(state.playing?'Ⅱ':'▶');
    if(card)model.card.material.color.set(engine.worlds[card.world].card);
    for(const h of model.holes){h.target.visible=!!card?.faces[state.face].pat[ROWS[h.row]].includes(h.step);h.target.position.y=model.ringY+(card?.03:0);}
    model.cardStack.forEach(c=>c.visible=c.userData.id!==state.cardId);
    model.setPower(state.power);
    model.windowMat.emissiveIntensity=state.power?.50:0;model.windowLight.intensity=state.power?.8:0;
    model.setKnob(0,(state.vol-.3)/.62);
    model.setKnob(1,(state.bpm-60)/120);
  }
  engine.events.addEventListener('change',sync);engine.events.addEventListener('change',invalidate);controls.addEventListener('change',invalidate);
  function select(id){selection=id;notify('已拿起：点一个空格，或放回托盘',true);}
  function deselect(){selection=null;model.holes.forEach(h=>h.hover.visible=false);}
  function startPiece(id){if(engine.take(id)){select(id);return true;}return false;}
  function place(target){
    if(!engine.held)return;
    const landing=model.pieces.get(engine.held.block.id);
    if(target?.kind==='hole'){
      const okay=engine.drop(ROWS[target.row],target.step);
      notify(okay?'已放入':'这个格子已经有积木了');if(okay)landing.userData.popAt=performance.now()+120;
    }else if(target?.kind==='tray'){engine.drop();notify('积木已放回托盘');}
    else{engine.cancel();notify('已放回原位');}
    deselect();sync();
  }
  function activate(hit){
    if(!hit)return;
    if(hit.kind==='power'){deselect();engine.power();return;}
    if(!engine.state.power)return;
    if(hit.kind==='play')action(()=>engine.toggle());
    if(hit.kind==='pad')action(()=>engine.hit(hit.row));
    if(hit.kind==='world')action(()=>engine.world(hit.world));
    if(hit.kind==='card')engine.flip();
    if(hit.kind==='cardStack')engine.card(hit.id);
  }
  // One gesture owner for mouse, touch and physical controls.
  const touches=new Map();let pinching=false,pinchDistance=0;
  canvas.addEventListener('pointerdown',e=>{
    if(e.pointerType!=='touch')return;
    touches.set(e.pointerId,new T.Vector2(e.clientX,e.clientY));
    if(touches.size>1){
      if(pointer)end({pointerId:pointer.id},true);
      engine.cancel();deselect();pinching=true;cameraGoal=null;
      canvas.setPointerCapture(e.pointerId);
      const points=[...touches.values()];pinchDistance=points[0].distanceTo(points[1]);
      e.preventDefault();e.stopImmediatePropagation();
    }
  },true);
  canvas.addEventListener('pointermove',e=>{
    if(touches.has(e.pointerId))touches.set(e.pointerId,new T.Vector2(e.clientX,e.clientY));
    if(!pinching)return;
    if(touches.size>=2){const points=[...touches.values()],distance=points[0].distanceTo(points[1]);if(distance>5&&pinchDistance>5)zoom(pinchDistance/distance);pinchDistance=distance;}
    e.preventDefault();e.stopImmediatePropagation();
  },true);
  function releaseTouch(e){touches.delete(e.pointerId);if(!pinching)return;if(canvas.hasPointerCapture(e.pointerId))canvas.releasePointerCapture(e.pointerId);if(!touches.size)pinching=false;e.stopImmediatePropagation();}
  canvas.addEventListener('pointerup',releaseTouch,true);canvas.addEventListener('pointercancel',releaseTouch,true);
  canvas.addEventListener('wheel',e=>{e.preventDefault();zoom(Math.exp(T.MathUtils.clamp(e.deltaY,-200,200)*.001));},{passive:false});

  canvas.addEventListener('pointerdown',e=>{
    if(e.button!==0)return;
    if(pointer){end({pointerId:pointer.id},true);e.stopImmediatePropagation();return;}
    let hit=pick(e);
    if(selection && hit && !['piece','hole','tray'].includes(hit.kind)){engine.cancel();deselect();sync();}
    if(selection && hit?.kind==='piece')hit=pick(e,true);
    if(!hit && !selection)hit={kind:'orbit'};
    if(!engine.state.power && hit?.kind!=='power')hit={kind:'orbit'};
    if(selection && hit?.kind==='hole' && engine.state.grid[ROWS[hit.row]][hit.step])return;
    if(hit?.kind==='hole' && !selection)hit={kind:'orbit'};
    if(hit?.kind==='tray' && !selection)hit={kind:'orbit'};
    e.stopImmediatePropagation();e.preventDefault();controls.enabled=false;cameraGoal=null;
    canvas.setPointerCapture(e.pointerId);canvas.focus({preventScroll:true});
    pointer={id:e.pointerId,x:e.clientX,y:e.clientY,hit,moved:false,wasSelected:!!selection,startVolume:engine.state.vol,startTempo:engine.state.bpm,spherical:new T.Spherical().setFromVector3(camera.position.clone().sub(controls.target))};
    if(hit?.kind==='piece'&&!selection)startPiece(hit.id);
    if(engine.held){
      const obj=model.pieces.get(engine.held.block.id);
      const grabY=hit?.point?.y??obj.position.y;
      dragPlane.constant=-grabY;setRay(e);
      if(raycaster.ray.intersectPlane(dragPlane,intersection))pointer.offset=obj.position.clone().sub(intersection);
    }

    if(hit?.kind==='pad')activate(hit);
    if(hit?.kind==='world')pointer.long=setTimeout(()=>{if(pointer&&!pointer.moved&&pointer.inside!==false){pointer.longFired=true;engine.bgmOff();notify('伴奏已关闭，保留当前音色');}},600);
  },true);
  canvas.addEventListener('pointermove',e=>{
    if(pointer && pointer.id!==e.pointerId)return;
    invalidate();
    if(pointer){
      const distance=Math.hypot(e.clientX-pointer.x,e.clientY-pointer.y);pointer.moved ||= distance>5;
      // Only buttons need the press-inside check; skip the raycast for orbit, knob and piece drags.
      if(pointer.hit&&!['orbit','volume','tempo','piece'].includes(pointer.hit.kind)&&!engine.held)pointer.inside=pick(e)?.object===pointer.hit.object;
      if(pointer.moved||!pointer.inside)clearTimeout(pointer.long);
      if(pointer.hit?.kind==='orbit'){
        const spherical=pointer.spherical.clone();spherical.theta-=(e.clientX-pointer.x)*.006;spherical.phi=T.MathUtils.clamp(spherical.phi-(e.clientY-pointer.y)*.006,controls.minPolarAngle,controls.maxPolarAngle);
        camera.position.copy(controls.target).add(new T.Vector3().setFromSpherical(spherical));controls.update();return;
      }
      if(pointer.hit?.kind==='volume'){engine.volume(pointer.startVolume+(pointer.y-e.clientY+e.clientX-pointer.x)*.004);notify('音量 '+Math.round(engine.state.vol*100)+'%');return;}
      if(pointer.hit?.kind==='tempo'){engine.tempo(pointer.startTempo+(pointer.y-e.clientY+e.clientX-pointer.x)*.6);notify(Math.round(engine.state.bpm)+' BPM');return;}
      if(engine.held && pointer.moved){
        setRay(e);if(raycaster.ray.intersectPlane(dragPlane,intersection)){
          const p=model.pieces.get(engine.held.block.id);p.position.copy(intersection).add(pointer.offset||new T.Vector3());p.position.y=model.lift;
        }
      }
    }
    hover=pointer?.moved&&engine.held?dragTarget():pick(e,!!selection);
    canvas.style.cursor=selection?'grabbing':hover&&hover.kind!=='hole'?'pointer':'grab';
    model.holes.forEach(h=>h.hover.visible=!!engine.held&&hover?.kind==='hole'&&h.row===hover.row&&h.step===hover.step);
    if(engine.held&&hover?.kind==='hole'){
      const h=model.holes.find(h=>h.row===hover.row&&h.step===hover.step);
      h.hover.material.color.set(!engine.state.grid[ROWS[hover.row]][hover.step]?0xf4d577:0xc88063);
      h.hover.position.y=model.ringY+.04;
    }
  });
  function dragTarget(){
    const obj=model.pieces.get(engine.held?.block.id);if(!obj)return null;
    for(const h of model.holes){const pos=gridPoint(h.row,h.step);if(Math.hypot(obj.position.x-pos.x,obj.position.z-pos.z)<.37)return{kind:'hole',row:h.row,step:h.step};}
    if(obj.position.x>-5.66&&obj.position.x<5.78&&obj.position.z>4.02&&obj.position.z<6.14)return{kind:'tray'};
    return null;
  }
  function end(e,cancelled=false){
    if(!pointer||pointer.id!==e.pointerId)return;
    const p=pointer;pointer=null;clearTimeout(p.long);
    if(canvas.hasPointerCapture(e.pointerId))canvas.releasePointerCapture(e.pointerId);controls.enabled=true;
    if(cancelled){engine.cancel();deselect();sync();return;}
    if(engine.held){
      if(p.moved)place(dragTarget());
      else if(p.wasSelected){const target=pick(e,true);if(target?.kind==='hole'||target?.kind==='tray')place(target);}
    }else if(p.hit?.kind==='card'&&p.moved){if(pick(e)?.kind!=='card')engine.card(null);}
    else if(!p.moved&&!p.longFired&&p.hit?.kind!=='pad'&&pick(e)?.object===p.hit?.object)activate(p.hit);
  }
  canvas.addEventListener('pointerup',e=>end(e));canvas.addEventListener('pointercancel',e=>end(e,true));
  canvas.addEventListener('lostpointercapture',e=>{if(pointer?.id===e.pointerId)end(e,true);});
  window.addEventListener('blur',()=>{touches.clear();pinching=false;if(pointer){clearTimeout(pointer.long);pointer=null;controls.enabled=true;}engine.cancel();deselect();});
  canvas.addEventListener('contextmenu',e=>e.preventDefault());

  document.addEventListener('keydown',e=>{
    if(e.repeat||e.metaKey||e.ctrlKey||e.altKey||e.target.matches('input,select,textarea'))return;
    if(e.key==='Escape'){engine.cancel();deselect();sync();return;}
    if(['Delete','Backspace'].includes(e.key)&&selection){e.preventDefault();place({kind:'tray'});return;}
    const row=['a','s','d','f'].indexOf(e.key.toLowerCase());if(row>=0){e.preventDefault();action(()=>engine.hit(row));}
    if(e.key===' '&&(e.target===canvas||e.target===document.body)){e.preventDefault();action(()=>engine.toggle());}
  });
  canvas.addEventListener('webglcontextlost',e=>{e.preventDefault();renderer.setAnimationLoop(null);if(engine.state.playing||engine.starting)action(()=>engine.toggle());notify('画面暂停了，请刷新页面恢复三维显示',true);});
  let previous=0,warmup=20,lastLit=-1,litAt=0;
  // A small hop: pieces bounce when the light reaches their column, and pop when they land.
  const hop=t=>t>0&&t<.32?Math.sin(Math.PI*t/.32)*(1-t/.32*.35):0;
  function frame(now){
    const measured=previous?(now-previous)/1000:0,dt=measured?Math.min(measured,.1):1/60;previous=now;engine.tick();
    if(cameraGoal){camera.position.lerp(cameraGoal,1-Math.exp(-dt*7));if(camera.position.distanceTo(cameraGoal)<.01)cameraGoal=null;}
    let active=controls.update()||!!cameraGoal||!!pointer;
    floor.visible=catcher.visible=contact.visible=camera.position.y>0;
    for(const [id,p]of model.pieces){
      if(engine.held?.block.id===id){active=true;if(!pointer?.moved){const original=engine.held.track?gridPoint(ROWS.indexOf(engine.held.track),engine.held.step):trayPoint(id);original.y+=.48;p.position.lerp(original,reduced?1:.22);}continue;}
      if(p.userData.target&&p.position.distanceToSquared(p.userData.target)>1e-6){active=true;p.position.lerp(p.userData.target,reduced?1:1-Math.exp(-dt*16));}
    }
    const step=engine.litStep,state=engine.state;
    if(step!==lastLit){lastLit=step;litAt=now;}
    for(const [id,p]of model.pieces){
      if(engine.held?.block.id===id||!p.userData.target)continue;
      const tgt=p.userData.target;if(Math.hypot(p.position.x-tgt.x,p.position.z-tgt.z)>.05)continue;
      const place=state.playing&&!reduced?engine.locate(id):null;
      let h=place?.track&&place.step===step?hop((now-litAt)/1000):0;
      if(p.userData.popAt&&!reduced)h=Math.max(h,1.25*hop((now-p.userData.popAt)/1000));
      if(h>0)active=true;
      p.position.y=tgt.y+h*.16;p.scale.set(1-h*.05,1+h*.1,1-h*.05);
    }
    active||=state.playing||engine.starting||!!state.pendingBgm||engine.pulseAt.some(t=>now-t<200);
    if(!active&&dirty<=0){previous=0;return;}
    dirty--;
    if(warmup>0)warmup--;else if(measured&&quality<2){slowFrames.push(measured);if(slowFrames.length>=40){slowFrames.sort((a,b)=>a-b);if(slowFrames[20]>.034)degrade(quality+1);slowFrames.length=0;}}
    model.leds.forEach((led,i)=>{led.material.emissiveIntensity=state.power&&state.playing&&i===step?2.5:0;});
    model.pads.forEach((pad,i)=>{const active=state.power&&(now-engine.pulseAt[i]<120||(pointer?.hit?.kind==='pad'&&pointer.hit.row===i&&pointer.inside!==false));model.press(pad,active);pad.material.emissive.set(COLORS[i]);pad.material.emissiveIntensity=active?.20:0;});
    model.worldKeys.forEach(key=>model.press(key,pointer?.hit?.object===key&&pointer.inside!==false));
    model.press(model.play,pointer?.hit?.kind==='play'&&pointer.inside!==false);
    model.worldLeds.forEach((led,i)=>{
      const pending=state.pendingBgm,world=pending?.world||state.bgm,slot=world?state.bgVariant[world]:0;
      led.material.emissive.set(world?engine.worlds[world].swatch:COLORS[i]);
      const on=pending?(pending.kind==='cross'||i===pending.variant?Math.sin(now*.009)>0:i===slot):!!world&&i===slot;
      led.material.emissiveIntensity=state.power&&on?2.3:0;
    });
    model.playRing.material.emissiveIntensity=state.power&&state.playing?1.3:0;
    composer.render();
  }
  if(quality)degrade(quality);
  engine.demo();sync();for(const p of model.pieces.values())if(p.userData.target)p.position.copy(p.userData.target);
  renderer.setAnimationLoop(frame);$('loading').hidden=true;
}

function studioEnvironment(){
  const s=new T.Scene();
  s.add(new T.Mesh(new T.BoxGeometry(24,14,24),new T.MeshBasicMaterial({color:0x7d878b,side:T.BackSide})));
  const panel=(w,h,color,power,pos)=>{const m=new T.Mesh(new T.PlaneGeometry(w,h),new T.MeshBasicMaterial({color:new T.Color(color).multiplyScalar(power),side:T.DoubleSide}));m.position.set(...pos);m.lookAt(0,0,0);s.add(m);};
  panel(9,6,0xfff0da,4.5,[-7,6,6]);   // warm key softbox
  panel(3,9,0xdbe8ff,2.4,[10,2,3]);   // cool strip
  panel(14,6,0xffffff,1.6,[0,6.9,-3]);// ceiling bounce
  panel(24,24,0xd8cdb8,.55,[0,-6.9,0]);// warm table bounce
  return s;
}
